/**
 * English - `dashboard`: the dashboard home (`home.*`), the setup guide
 * (`setup.*`) and the settings overview (`settings.*`).
 *
 * Keys: `home.ranges.<range>` by the `range` query value; `home.kpi.<snake>`
 * for the figure tiles. `setup.steps.<STEP>.name|summary|title|description|
 * tip|action` by the backend's `OnboardingStep` value, which is also the step's
 * `key` in `features/dashboard/lib/setupSteps.ts`. Conventions: see
 * `common.ts`.
 */
const dashboard: Record<string, string> = {
  'home.ranges.today': 'Today',
  'home.ranges.7d': '7 days',
  'home.ranges.30d': '30 days',
  'home.ranges.90d': '90 days',
  'home.greeting': 'Good day, {name} 👋',
  'home.greeting_fallback_name': 'there',
  'home.business_summary': 'Here is where {name} stands today.',
  'home.loading_business': 'Loading your business…',
  'home.kpi.revenue': 'Revenue',
  'home.kpi.orders': 'Orders',
  'home.kpi.average_order': 'Average order',
  'home.kpi.new_customers': 'New customers',
  'home.full_report': 'Full report',
  'home.top_products.title': 'Top products',
  'home.top_products.description': 'Your best sellers in this window.',
  'home.top_products.units_sold_one': '{count} sold',
  'home.top_products.units_sold_other': '{count} sold',
  'home.top_products.empty': 'No sales in this window yet.',
  'home.running_low.title': 'Running low',
  'home.running_low.description': 'Restock these before they sell out.',
  'home.running_low.left_one': '{count} left',
  'home.running_low.left_other': '{count} left',
  'home.running_low.empty': 'Everything is comfortably in stock.',

  'setup.greeting.morning': 'Good morning, {name} 👋',
  'setup.greeting.afternoon': 'Good afternoon, {name} 👋',
  'setup.greeting.evening': 'Good evening, {name} 👋',
  'setup.title': 'Let’s get your store ready to sell.',
  'setup.subtitle': 'Follow these {total} simple steps to set up your store and start selling to customers.',
  'setup.progress': '{completed} of {total} steps completed',
  'setup.percent_complete': '{percent}%',
  'setup.time_remaining': 'Estimated time remaining: ~{minutes} minutes',
  'setup.video.title': 'Make your online shop in 15 minutes',
  'setup.video.play': 'Play video: {title} ({duration})',
  'setup.video.close': 'Close video',
  'setup.video.youtube': 'Watch on YouTube',
  'setup.video.more': 'More help videos',
  'setup.video.watch_step': 'Watch how ({time})',
  'setup.list.title': 'Complete your store setup',
  'setup.list.count': '{total} steps',
  'setup.status.completed': 'Completed',
  'setup.status.skipped': 'Skipped',
  'setup.skip': 'Skip',
  'setup.skip_failed': 'Could not update this step. Please try again.',
  'setup.launch_samples.title': 'Replace or clear sample content before you launch',
  'setup.launch_samples.description': 'Your store still has sample content added when it was created - products, team members, locations or blog posts. Edit them into your own or remove them, so shoppers only see what is really yours.',
  'setup.launch_samples.action': 'Review products',
  'setup.continue': 'Continue',
  'setup.duration': '{minutes} min',
  'setup.permission_required': 'Requires owner or manager permission',
  'setup.detail.step_of': 'Step {step} of {total}',
  'setup.detail.assistance': 'Store setup assistance',
  'setup.detail.continue': 'Continue Setup',
  'setup.detail.review': 'Review step',
  'setup.detail.requires_permission': 'Requires owner or manager permission',
  'setup.complete.title': 'Your store is ready to sell.',
  'setup.complete.description':
    'All {total} setup steps are done. Your store can take orders, collect payments and ship to customers.',
  'setup.complete.view_storefront': 'View Storefront',
  'setup.complete.manage_products': 'Manage Products',

  'setup.steps.BUSINESS_CREATED.name': 'Business information',
  'setup.steps.BUSINESS_CREATED.summary': 'Add your business name, address and contact details.',
  'setup.steps.BUSINESS_CREATED.title': 'Add your business information',
  'setup.steps.BUSINESS_CREATED.description':
    'Tell customers who you are. Your business name, address and contact details appear on invoices, notifications and your storefront footer.',
  'setup.steps.BUSINESS_CREATED.tip':
    'Keep your contact details current so customers and couriers can reach you when they need to.',
  'setup.steps.BUSINESS_CREATED.action': 'Review business info',
  'setup.steps.STORE_CREATED.name': 'Store setup',
  'setup.steps.STORE_CREATED.summary': 'Set your store name, logo, URL and basic settings.',
  'setup.steps.STORE_CREATED.title': 'Set up your store',
  'setup.steps.STORE_CREATED.description':
    'Give your store a name and configure its basic settings. You can change these later anytime.',
  'setup.steps.STORE_CREATED.tip':
    'Your store name, logo and URL help customers recognize your brand. You can customize more settings after this step.',
  'setup.steps.STORE_CREATED.action': 'Configure store',
  'setup.steps.THEME_SELECTED.name': 'Design your store',
  'setup.steps.THEME_SELECTED.summary': 'Choose a theme and customize your storefront.',
  'setup.steps.THEME_SELECTED.title': 'Design your store',
  'setup.steps.THEME_SELECTED.description':
    'Pick a theme, then make it yours with your colors, fonts, banners and layout.',
  'setup.steps.THEME_SELECTED.tip':
    'Every theme is responsive and built for fast mobile checkout, so start with the look you like best.',
  'setup.steps.THEME_SELECTED.action': 'Customize theme',
  'setup.steps.PRODUCTS_ADDED.name': 'Add products',
  'setup.steps.PRODUCTS_ADDED.summary': 'Add your products, pricing and inventory.',
  'setup.steps.PRODUCTS_ADDED.title': 'Add your products',
  'setup.steps.PRODUCTS_ADDED.description':
    'Add products with photos, descriptions, variants, pricing and stock so customers have something to buy.',
  'setup.steps.PRODUCTS_ADDED.tip':
    'Adding at least 3-5 products makes your storefront feel full and welcoming.',
  'setup.steps.PRODUCTS_ADDED.action': 'Add products',
  'setup.steps.PAYMENTS_CONFIGURED.name': 'Payments & checkout',
  'setup.steps.PAYMENTS_CONFIGURED.summary': 'Connect Razorpay or another payment provider.',
  'setup.steps.PAYMENTS_CONFIGURED.title': 'Connect payments',
  'setup.steps.PAYMENTS_CONFIGURED.description':
    'Connect Razorpay or another provider to accept UPI, cards and Cash on Delivery at checkout.',
  'setup.steps.PAYMENTS_CONFIGURED.tip':
    'Offering UPI and cards reduces abandoned carts and gets money to you faster.',
  'setup.steps.PAYMENTS_CONFIGURED.action': 'Connect payments',
  'setup.steps.SHIPPING_CONFIGURED.name': 'Shipping & delivery',
  'setup.steps.SHIPPING_CONFIGURED.summary': 'Configure shipping zones, rates and delivery options.',
  'setup.steps.SHIPPING_CONFIGURED.title': 'Set up shipping',
  'setup.steps.SHIPPING_CONFIGURED.description':
    'Choose where you deliver, what it costs and how quickly orders reach customers.',
  'setup.steps.SHIPPING_CONFIGURED.tip':
    'Clear delivery timelines build buyer trust and improve checkout conversion.',
  'setup.steps.SHIPPING_CONFIGURED.action': 'Configure shipping',
  'setup.steps.SHIPPING_CONFIGURED.skip.title': 'Don’t ship anything?',
  'setup.steps.SHIPPING_CONFIGURED.skip.description':
    'Clinics, restaurants, digital and local service businesses can skip this step. You can set up shipping any time later.',
  'setup.steps.SHIPPING_CONFIGURED.skip.action': 'Skip shipping',
  'setup.steps.SHIPPING_CONFIGURED.skipped.title': 'Shipping skipped',
  'setup.steps.SHIPPING_CONFIGURED.skipped.description':
    'Your store works without delivery. Shipping settings stay available whenever you want to start delivering.',
  'setup.steps.SHIPPING_CONFIGURED.skipped.undo': 'Undo skip',
  'setup.steps.LAUNCHED.name': 'Launch your store',
  'setup.steps.LAUNCHED.summary': 'Review everything and make your store live.',
  'setup.steps.LAUNCHED.title': 'Launch your store',
  'setup.steps.LAUNCHED.description':
    'Review everything and publish your store so customers can start buying. You can connect a custom domain afterwards.',
  'setup.steps.LAUNCHED.tip':
    'Once you are live, share product links on WhatsApp, Instagram and your other channels.',
  'setup.steps.LAUNCHED.action': 'Launch store',

  'settings.business.title': 'Business',
  'settings.business.description': 'Details used across your storefront, invoices and notifications.',
  'settings.business.fields.name': 'Name',
  'settings.business.fields.slug': 'Storefront address',
  'settings.business.fields.email': 'Contact email',
  'settings.business.fields.phone': 'Phone',
  'settings.business.fields.city': 'City',
  'settings.business.fields.state': 'State',
  'settings.business.fields.gstin': 'GSTIN',
  'settings.business.fields.currency': 'Currency',
  'settings.business.fields.timezone': 'Timezone',
  'settings.business.save': 'Save details',
  'settings.business.saved': 'Business details updated.',
  'settings.business.save_failed': 'Your business details could not be updated.',
  'settings.business.phone_invalid': 'Enter a 10-digit Indian mobile number.',
  'settings.business.gstin_invalid': 'Enter a 15-character GSTIN, like 22AAAAA0000A1Z5.',
  'settings.seller.title': 'Seller details on product pages',
  'settings.seller.description': 'The details Indian e-commerce rules expect a product page to show: your legal name, address, GSTIN, FSSAI licence for food and customer care.',
  'settings.seller.legal_name': 'Legal name',
  'settings.seller.fssai_number': 'FSSAI licence number',
  'settings.seller.fssai_invalid': 'Enter the 14-digit FSSAI licence number.',
  'settings.seller.show': 'Show seller details on product pages',
  'settings.seller.show_note': 'When on, your legal name, business address, GSTIN, FSSAI number, support email and phone are public on every product page that has the Seller information panel.',
  'settings.seller.save': 'Save seller details',
  'settings.seller.saved': 'Seller details updated.',
  'settings.seller.save_failed': 'Your seller details could not be updated.',
  'settings.seller.load_failed': 'Your seller details could not be loaded.',
  'settings.address.title': 'Business address',
  'settings.address.description': 'Your registered business address. It is printed on your invoices.',
  'settings.address.line1': 'Address line 1',
  'settings.address.line2': 'Address line 2',
  'settings.address.city': 'City',
  'settings.address.state': 'State',
  'settings.address.postal_code': 'PIN code',
  'settings.address.postal_invalid': 'Enter a 6-digit PIN code.',
  'settings.address.save': 'Save address',
  'settings.address.saved': 'Address updated.',
  'settings.address.save_failed': 'Your address could not be updated.',
  'settings.address.load_failed': 'Your business address could not be loaded.',
  'settings.address.empty': 'No address added yet.',
  'settings.address.read_only': 'Only people who can edit business details can change it.',
  'settings.industry.title': 'Industry',
  'settings.industry.description':
    'Shapes the category suggestions and sample products we offer. Changing it does not change the products or categories you already have.',
  'settings.industry.label': 'Your industry',
  'settings.industry.none': 'Not set',
  'settings.industry.saved': 'Industry updated.',
  'settings.industry.save_failed': 'Your industry could not be updated.',
  'settings.security.title': 'Security',
  'settings.security.description':
    'See every device signed in to your account, and sign out the ones you do not recognise.',
  'settings.security.link': 'Signed-in devices →',
  'settings.team.title': 'Team',
  'settings.team.description': 'People who can sign in to this business.',
};

export default dashboard;
